import React, { useState, useEffect } from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { useAuth } from "../AuthContext";
import {
  Truck, ChartBar, Buildings, MapTrifold, IdentificationCard, Receipt, SignOut, List, X,
  Broadcast, UsersThree, CurrencyDollar, Plugs, FileText, ChatCircleDots, Scales,
  VideoCamera, Signature,
} from "@phosphor-icons/react";
import { useState as useReactState } from "react";
import api from "../api";

const NAV = [
  { to: "/admin", end: true, label: "Dashboard", icon: ChartBar, testid: "nav-dashboard", visitor: true },
  { to: "/admin/customers", label: "Customers", icon: Buildings, testid: "nav-customers", visitor: true },
  { to: "/admin/routes", label: "Routes", icon: MapTrifold, testid: "nav-routes", visitor: true },
  { to: "/admin/fleet", label: "Live Fleet", icon: Broadcast, testid: "nav-fleet" },
  { to: "/admin/dashcam", label: "Dash Cam", icon: VideoCamera, testid: "nav-dashcam" },
  { to: "/admin/billing", label: "Billing Hub", icon: CurrencyDollar, testid: "nav-billing" },
  { to: "/admin/documents", label: "Documents", icon: FileText, testid: "nav-documents" },
  { to: "/admin/disposal-tickets", label: "Disposal Tickets", icon: Scales, testid: "nav-disposal-tickets", visitor: true },
  { to: "/admin/customer-issues", label: "Customer Issues", icon: ChatCircleDots, testid: "nav-customer-issues", badgeKey: "customer_issues" },
  { to: "/admin/users", label: "User Management", icon: UsersThree, testid: "nav-users" },
  { to: "/admin/reports", label: "Reports", icon: Receipt, testid: "nav-reports" },
  { to: "/admin/integrations", label: "Integrations", icon: Plugs, testid: "nav-integrations" },
  { to: "/admin/email-signature", label: "Email Signature", icon: Signature, testid: "nav-email-signature" },
];

/** Poll a lightweight summary endpoint to power sidebar badges. */
function useSidebarBadges(disabled) {
  const [badges, setBadges] = useReactState({});
  useEffect(() => {
    if (disabled) return undefined;
    let alive = true;
    async function tick() {
      try {
        const { data } = await api.get("/customer-issues/summary");
        if (alive) setBadges((b) => ({ ...b, customer_issues: data.unassigned_new || 0 }));
      } catch {
        /* ignore — user may not be admin yet or endpoint offline */
      }
    }
    tick();
    const iv = setInterval(tick, 60000);
    return () => { alive = false; clearInterval(iv); };
  }, [disabled]);
  return badges;
}

export default function AdminLayout() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const [open, setOpen] = useState(false);
  const isVisitor = user?.role === "visitor";
  // Skip the customer-issues badge poll for visitors — they don't have access
  // to that endpoint, so the request just spams the console with 403s.
  const badges = useSidebarBadges(isVisitor);
  const visibleNav = NAV.filter((n) => !isVisitor || n.visitor);

  // Belt-and-suspenders SEO hardening: authentication already blocks
  // unauthorised users, but if a search-engine crawler ever reaches an
  // /admin/* URL via a shared link we still want `noindex, nofollow`
  // in the served DOM. Applied dynamically because the app is a single
  // BrowserRouter and the base index.html is otherwise indexable.
  useEffect(() => {
    const tag = document.createElement("meta");
    tag.name = "robots";
    tag.content = "noindex, nofollow, noarchive, nosnippet";
    tag.setAttribute("data-zmex-admin-meta", "true");
    document.head.appendChild(tag);
    const prevTitle = document.title;
    if (!/ZMEX Hub/i.test(prevTitle)) document.title = "ZMEX Hub";
    return () => { tag.remove(); };
  }, []);

  async function doLogout() {
    await logout();
    nav("/login", { replace: true });
  }

  return (
    <div className="h-screen overflow-hidden bg-[#FAFAFA] flex" data-testid="admin-shell">
      {/* Sidebar */}
      <aside
        className={`fixed md:static z-40 inset-y-0 left-0 w-64 bg-[#012340] text-white flex-col transition-transform ${
          open ? "flex translate-x-0" : "hidden md:flex md:translate-x-0"
        }`}
        data-testid="admin-sidebar"
      >
        <div className="flex items-center gap-3 px-5 py-5 border-b border-white/10">
          <div className="w-9 h-9 bg-[#CFAB7A] rounded-sm grid place-items-center text-[#012340]">
            <Truck size={20} weight="bold" />
          </div>
          <div>
            <div className="font-heading font-black tracking-tight">ZMEX HUB</div>
            <div className="text-[10px] uppercase tracking-[0.3em] text-[#CFAB7A]">A ZMEX Disposal Company</div>
          </div>
        </div>
        <nav className="flex-1 px-3 py-4 space-y-1">
          {isVisitor && (
            <div className="mb-3 px-3 py-2 rounded-sm bg-[#CFAB7A]/10 border border-[#CFAB7A]/25 text-[10px] uppercase tracking-[0.24em] text-[#CFAB7A] font-bold" data-testid="visitor-banner">
              Mentor View · read-only
            </div>
          )}
          {visibleNav.map((n) => {
            const Icon = n.icon;
            const badgeCount = n.badgeKey ? badges[n.badgeKey] || 0 : 0;
            return (
              <NavLink
                key={n.to}
                to={n.to}
                end={n.end}
                onClick={() => setOpen(false)}
                className={({ isActive }) => `sidebar-link ${isActive ? "active" : ""}`}
                data-testid={n.testid}
              >
                <Icon size={18} weight="bold" />
                <span className="flex-1">{n.label}</span>
                {badgeCount > 0 && (
                  <span
                    className="inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 rounded-full bg-[#E53935] text-white text-[10px] font-bold"
                    data-testid={`${n.testid}-badge`}
                  >
                    {badgeCount > 99 ? "99+" : badgeCount}
                  </span>
                )}
              </NavLink>
            );
          })}
        </nav>
        <div className="p-3 border-t border-white/10">
          <div className="px-3 py-2 text-xs">
            <div className="text-white/60 uppercase tracking-[0.2em]">Signed in</div>
            <div className="text-white font-semibold mt-1">{user?.name}</div>
            <div className="text-white/50">{user?.email}</div>
          </div>
          <button
            className="sidebar-link w-full justify-start mt-2"
            onClick={doLogout}
            data-testid="admin-logout"
          >
            <SignOut size={18} weight="bold" /> Log out
          </button>
        </div>
      </aside>

      {/* Mobile overlay */}
      {open && (
        <div className="md:hidden fixed inset-0 bg-black/40 z-30" onClick={() => setOpen(false)} />
      )}

      {/* Main — fixed header, scrollable body. Sidebar stays anchored. */}
      <div className="flex-1 flex flex-col min-w-0 min-h-0">
        <header className="flex-shrink-0 bg-white border-b border-[#012340]/10 px-5 md:px-8 py-4 flex items-center justify-between">
          <button
            className="md:hidden p-2 rounded-sm border border-[#012340]/15"
            onClick={() => setOpen((s) => !s)}
            data-testid="sidebar-toggle"
          >
            {open ? <X size={18} weight="bold" /> : <List size={18} weight="bold" />}
          </button>
          <div className="text-xs uppercase tracking-[0.3em] text-[#5C738A]">Admin Control Room</div>
          <div className="text-sm text-[#012340] font-semibold hidden sm:block">{user?.name}</div>
        </header>
        <main className="p-4 sm:p-5 md:p-8 flex-1 overflow-y-auto overflow-x-hidden min-h-0 min-w-0" data-testid="admin-main">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
